import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { BookOpen, ChevronDown, HelpCircle } from 'lucide-react'
import { formatPrice } from '../../../../utils/formatters'
import { HALLOWEEN_MODES } from '../../utils/halloween'
import { CARD_CLASS, PumpkinIcon } from './shared'

// Вопросы FAQ: тексты в halloweenEvent.faq.<id>.q / .a
const FAQ_IDS = ['noPumpkin', 'cap', 'carry', 'leftTeam', 'reward', 'where', 'first', 'twoPrizes', 'witch', 'fair']

const Collapsible = ({ icon, title, children }) => {
  const [open, setOpen] = useState(false)
  return (
    <section className={`${CARD_CLASS} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-5 text-left transition-colors hover:bg-white/[0.02]"
      >
        {icon}
        <span className="flex-1 text-sm font-bold text-white">{title}</span>
        <ChevronDown size={18} className={`shrink-0 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

const RulesBlock = ({ title, children }) => (
  <div className="mt-4 first:mt-0">
    <h3 className="text-xs font-black uppercase not-italic tracking-wide text-orange-300">{title}</h3>
    <div className="mt-1.5 space-y-1.5 text-sm leading-relaxed text-white/85">{children}</div>
  </div>
)

const Bullet = ({ children }) => (
  <p className="flex items-start gap-2">
    <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-orange-400" />
    <span>{children}</span>
  </p>
)

/**
 * Правила и FAQ эвента. Все числа берутся из rules / prizes / ladder ответа,
 * поэтому изменение настроек на бэкенде не требует нового перевода.
 */
const RulesCard = ({ rules, prizes, ladder = [] }) => {
  const { t } = useTranslation()
  if (!rules) return null

  const modes = (rules.modes || []).filter((mode) => HALLOWEEN_MODES.includes(mode))
  const superText = [...(prizes?.super || [])]
    .sort((a, b) => a.place - b.place)
    .map((prize) => t('halloweenEvent.rules.superItem', { place: prize.place, amount: formatPrice(prize.amount) }))
    .join(', ')
  const witchCount = prizes?.witch?.count ?? 0
  const witchAmount = formatPrice(prizes?.witch?.amount ?? 0)
  const faqValues = {
    noPumpkin: {},
    cap: { count: rules.daily_cap },
    carry: {},
    leftTeam: {},
    reward: { count: rules.min_contributors },
    where: {},
    first: {},
    twoPrizes: {},
    witch: { count: witchCount },
    fair: {}
  }

  return (
    <div className="space-y-4">
      <Collapsible icon={<BookOpen size={20} className="shrink-0 text-orange-300" />} title={t('halloweenEvent.rules.title')}>
        <p className="text-sm leading-relaxed text-white/85">{t('halloweenEvent.rules.intro')}</p>

        <RulesBlock title={t('halloweenEvent.rules.whenTitle')}>
          <p>{t('halloweenEvent.rules.when')}</p>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.whoTitle')}>
          <p>{t('halloweenEvent.rules.who')}</p>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.howTitle')}>
          <Bullet>{t('halloweenEvent.rules.howPrice', { price: formatPrice(rules.pumpkin_price) })}</Bullet>
          <Bullet>{t('halloweenEvent.rules.howModes')}</Bullet>
          <div className="flex flex-wrap gap-1.5 ps-3">
            {modes.map((mode) => (
              <span key={mode} className="rounded-lg border border-orange-400/25 bg-orange-500/10 px-2 py-0.5 text-xs font-semibold text-orange-100">
                {t(`halloweenEvent.modes.${mode}`)}
              </span>
            ))}
          </div>
          <Bullet>{t('halloweenEvent.rules.howShop')}</Bullet>
          <Bullet>{t('halloweenEvent.rules.howCap', { count: rules.daily_cap })}</Bullet>
          <Bullet>{t('halloweenEvent.rules.howTeam')}</Bullet>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.rewardsTitle')}>
          <p>{t('halloweenEvent.rules.rewards')}</p>
          <div className="space-y-1 rounded-xl border border-white/10 bg-black/25 p-2.5">
            {ladder.map((item) => (
              <p key={item.step} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-1.5">
                  <PumpkinIcon size={14} />
                  {t('halloweenEvent.pumpkins', { count: item.pumpkins })}
                </span>
                <span className="font-bold tabular-nums text-accent-primary">{formatPrice(item.reward)} 💎</span>
              </p>
            ))}
          </div>
          <p>{t('halloweenEvent.rules.rewardsCondition', { count: rules.min_contributors })}</p>
          <p>{t('halloweenEvent.rules.rewardsBurn')}</p>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.witchTitle')}>
          <p>{t('halloweenEvent.rules.witch', { goal: formatPrice(rules.witch_goal), count: witchCount, amount: witchAmount })}</p>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.drawTitle')}>
          <Bullet>{t('halloweenEvent.rules.drawTickets')}</Bullet>
          <Bullet>
            {t('halloweenEvent.rules.drawPrizes', {
              super: superText,
              witchCount,
              witchAmount,
              consolationCount: prizes?.consolation?.count ?? 0,
              consolationAmount: formatPrice(prizes?.consolation?.amount ?? 0)
            })}
          </Bullet>
          <Bullet>{t('halloweenEvent.rules.drawOne')}</Bullet>
          <Bullet>{t('halloweenEvent.rules.drawTreasury')}</Bullet>
          <Bullet>{t('halloweenEvent.rules.drawPlace')}</Bullet>
          <Bullet>{t('halloweenEvent.rules.drawFair')}</Bullet>
        </RulesBlock>

        <RulesBlock title={t('halloweenEvent.rules.payoutTitle')}>
          <p>{t('halloweenEvent.rules.payout')}</p>
        </RulesBlock>
      </Collapsible>

      <Collapsible icon={<HelpCircle size={20} className="shrink-0 text-violet-300" />} title={t('halloweenEvent.faq.title')}>
        <div className="space-y-3">
          {FAQ_IDS.map((id) => (
            <div key={id} className="rounded-xl border border-white/5 bg-black/20 p-3">
              <p className="text-sm font-bold text-white">{t(`halloweenEvent.faq.${id}.q`, faqValues[id])}</p>
              <p className="mt-1 text-sm leading-relaxed text-text-secondary">{t(`halloweenEvent.faq.${id}.a`, faqValues[id])}</p>
            </div>
          ))}
        </div>
      </Collapsible>
    </div>
  )
}

export default RulesCard
